/**
 * Gestione colori dinamici dal backend
 * I colori primary e secondary possono essere configurati dinamicamente
 */

export interface ThemeColors {
  primary: string;
  secondary: string;
}

/**
 * Ottiene i colori dal localStorage o dal backend
 */
export function getThemeColors(): ThemeColors {
  const primaryColor = localStorage.getItem('THEME_PRIMARY_COLOR') || '#12b5aa';
  const secondaryColor = localStorage.getItem('THEME_SECONDARY_COLOR') || '#ff6b5b';

  return {
    primary: primaryColor,
    secondary: secondaryColor,
  };
}

/**
 * Aggiorna i colori nel DOM per Tailwind
 * Questo permette di usare i colori dinamici con Tailwind CSS
 */
export function updateThemeColors(colors: ThemeColors): void {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', colors.primary);
  root.style.setProperty('--color-secondary', colors.secondary);
  
  // Calcola varianti light e dark
  const primaryLight = lightenColor(colors.primary, 0.2);
  const primaryDark = darkenColor(colors.primary, 0.2);
  const secondaryLight = lightenColor(colors.secondary, 0.2);
  const secondaryDark = darkenColor(colors.secondary, 0.2);

  root.style.setProperty('--color-primary-light', primaryLight);
  root.style.setProperty('--color-primary-dark', primaryDark);
  root.style.setProperty('--color-secondary-light', secondaryLight);
  root.style.setProperty('--color-secondary-dark', secondaryDark);
}

/**
 * Converte hex a RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Schiarisce un colore
 */
function lightenColor(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.min(255, Math.round(rgb.r + (255 - rgb.r) * amount));
  const g = Math.min(255, Math.round(rgb.g + (255 - rgb.g) * amount));
  const b = Math.min(255, Math.round(rgb.b + (255 - rgb.b) * amount));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Scurisce un colore
 */
function darkenColor(hex: string, amount: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const r = Math.max(0, Math.round(rgb.r * (1 - amount)));
  const g = Math.max(0, Math.round(rgb.g * (1 - amount)));
  const b = Math.max(0, Math.round(rgb.b * (1 - amount)));

  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}
